<template>
  <div class="markets-container">
    <div class="markets-header">
      <h1 class="markets-title">{{ $t('markets.title') }}</h1>
    </div>

    <div class="markets-controls">
      <div class="search-wrapper">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="$t('markets.searchPlaceholder')"
        />
      </div>

      <MarketSortBar
        :sort-key="sortKey"
        :sort-direction="sortDirection"
        @update:sortKey="(value) => (sortKey = value)"
        @update:sortDirection="(value) => (sortDirection = value)"
      />
    </div>

    <div class="markets-main">
      <!-- Левая часть: список рынков -->
      <div class="markets-content markets-left">
        <div v-if="isLoading" class="loading-state">
          <div class="loader-dots">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
          <p class="loading-text">{{ $t('markets.loadingMarkets') }}</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p class="error-title">{{ $t('markets.unableToLoad') }}</p>
          <p class="error-message">{{ error }}</p>
          <button class="retry-button" @click="loadMarkets">{{ $t('markets.tryAgain') }}</button>
        </div>

        <div v-else>
          <div v-if="filteredMarkets.length === 0" class="empty-state">
            <p class="empty-title">{{ $t('markets.nothingFound') }}</p>
            <p class="empty-message">
              {{ $t('markets.nothingFoundMessage') }}
            </p>
          </div>

          <ul v-else class="markets-list">
            <MarketListItem
              v-for="coin in filteredMarkets"
              :key="coin.id"
              :coin="coin"
              @select="openCoin"
            />
          </ul>

          <div
            v-if="hasMore && filteredMarkets.length > 0"
            class="load-more-wrapper"
          >
            <BaseButton
              variant="outline"
              size="sm"
              class="load-more-button"
              @click="loadMore"
              :disabled="isLoadingMore"
            >
              {{ isLoadingMore ? $t('markets.loading') : $t('markets.loadMore') }}
            </BaseButton>
          </div>
        </div>
      </div>

      <!-- Правая часть: плейсхолдер или детали рынка (только на десктопе видимы через CSS) -->
      <div class="markets-right">
        <div v-if="!selectedCoinId" class="markets-placeholder">
          <p class="markets-placeholder-title">{{ $t('markets.chooseAsset') }}</p>
          <p class="markets-placeholder-text">
            {{ $t('markets.chooseAssetMessage') }}
          </p>
        </div>
        <MarketDetailsScreen
          v-else
          :key="selectedCoinId"
          :coin-id="selectedCoinId"
          :embedded="true"
        />
      </div>
    </div>

    <BottomMenu />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import BottomMenu from '../components/ui/BottomMenu.vue'
import MarketSortBar from '../components/ui/MarketSortBar.vue'
import MarketListItem from '../components/ui/MarketListItem.vue'
import BaseButton from '../components/ui/BaseButton.vue'
import { marketApi } from '../services/marketApi.js'
import MarketDetailsScreen from './MarketDetailsScreen.vue'

export default {
  name: 'MarketsScreen',
  components: {
    BottomMenu,
    MarketSortBar,
    MarketListItem,
    BaseButton,
    MarketDetailsScreen
  },
  setup() {
    const router = useRouter()
    const markets = ref([])
    const isLoading = ref(false)
    const isLoadingMore = ref(false)
    const currentPage = ref(1)
    const perPage = 50
    const hasMore = ref(true)
    const error = ref('')
    const searchQuery = ref('')
    const sortKey = ref('volume24h')
    const sortDirection = ref('desc')
    const isDesktop = ref(false)
    const selectedCoinId = ref(null)

    const updateIsDesktop = () => {
      if (typeof window === 'undefined') return
      isDesktop.value = window.innerWidth >= 924
    }

    const loadMarkets = async () => {
      try {
        isLoading.value = true
        error.value = ''
        currentPage.value = 1
        hasMore.value = true

        const data = await marketApi.getMarkets({
          perPage,
          page: currentPage.value,
          order: 'market_cap_desc',
          sparkline: true
        })

        const list = Array.isArray(data) ? data : []
        markets.value = list
        if (!list.length || list.length < perPage) {
          hasMore.value = false
        }
      } catch (err) {
        console.error('Error loading markets:', err)
        error.value = 'Please try again in a few minutes.'
      } finally {
        isLoading.value = false
      }
    }

    const loadMore = async () => {
      if (!hasMore.value || isLoadingMore.value) return

      try {
        isLoadingMore.value = true

        const nextPage = currentPage.value + 1
        const data = await marketApi.getMarkets({
          perPage,
          page: nextPage,
          order: 'market_cap_desc',
          sparkline: true
        })

        const list = Array.isArray(data) ? data : []
        if (list.length) {
          markets.value = [...markets.value, ...list]
          currentPage.value = nextPage
          if (list.length < perPage) {
            hasMore.value = false
          }
        } else {
          hasMore.value = false
        }
      } catch (err) {
        console.error('Error loading more markets:', err)
      } finally {
        isLoadingMore.value = false
      }
    }

    const filteredMarkets = computed(() => {
      let result = markets.value

      const query = searchQuery.value.trim().toLowerCase()
      if (query) {
        result = result.filter((coin) => {
          return (
            coin.name.toLowerCase().includes(query) ||
            coin.symbol.toLowerCase().includes(query)
          )
        })
      }

      const direction = sortDirection.value === 'asc' ? 1 : -1

      result = [...result].sort((a, b) => {
        switch (sortKey.value) {
          case 'symbol':
            return (
              a.symbol.localeCompare(b.symbol) * direction
            )
          case 'price':
            return ((a.current_price || 0) - (b.current_price || 0)) * direction
          case 'change24h':
            return (
              ((a.price_change_percentage_24h || 0) -
                (b.price_change_percentage_24h || 0)) * direction
            )
          case 'volume24h':
          default:
            return ((a.total_volume || 0) - (b.total_volume || 0)) * direction
        }
      })

      return result
    })

    const formatPrice = (value) => {
      if (value == null) return '-'
      if (value >= 1000) {
        return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 2 })
      }
      if (value >= 1) {
        return '$' + value.toFixed(2)
      }
      if (value >= 0.01) {
        return '$' + value.toFixed(4)
      }
      return '$' + value.toFixed(6)
    }

    const formatNumber = (value) => {
      if (value == null) return '-'
      if (value >= 1_000_000_000) {
        return (value / 1_000_000_000).toFixed(1) + 'B'
      }
      if (value >= 1_000_000) {
        return (value / 1_000_000).toFixed(1) + 'M'
      }
      if (value >= 1_000) {
        return (value / 1_000).toFixed(1) + 'K'
      }
      return value.toLocaleString('en-US')
    }

    const formatChange = (value) => {
      if (value == null) return '-'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value.toFixed(2)}%`
    }

    const openCoin = (coin) => {
      try {
        sessionStorage.setItem('hl_selected_market', JSON.stringify(coin))
      } catch (e) {
        console.warn('Unable to cache selected market:', e)
      }

      if (isDesktop.value) {
        selectedCoinId.value = coin.id
      } else {
        router.push({
          name: 'MarketDetails',
          params: { id: coin.id }
        })
      }
    }

    onMounted(() => {
      loadMarkets()
      updateIsDesktop()
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', updateIsDesktop)
      }
    })

    onBeforeUnmount(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateIsDesktop)
      }
    })

    return {
      markets,
      isLoading,
      isLoadingMore,
      hasMore,
      error,
      searchQuery,
      sortKey,
      sortDirection,
      filteredMarkets,
      isDesktop,
      selectedCoinId,
      loadMarkets,
      loadMore,
      formatPrice,
      formatNumber,
      formatChange,
      openCoin
    }
  }
}
</script>

<style scoped>
.markets-container {
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
}

.markets-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
}

.markets-title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.markets-controls {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.search-wrapper {
  width: 100%;
}

.search-input {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  outline: none;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.8);
}

.search-input::placeholder {
  color: var(--color-text-secondary);
}

.sort-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.sort-chip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 10px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-bg-secondary);
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  cursor: pointer;
  transition: all 0.15s ease;
}

.sort-chip.active {
  border-color: var(--color-accent);
  color: var(--color-accent);
  box-shadow: 0 0 0 1px rgba(118, 223, 198, 0.25);
}

.sort-arrow {
  font-size: 10px;
}

.markets-content {
  margin-top: var(--spacing-sm);
}

.markets-main {
  margin-top: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.markets-left {
  min-width: 0;
}

.markets-right {
  display: none;
}

.markets-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--spacing-md);
  color: var(--color-text-secondary);
}

.markets-placeholder-title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.markets-placeholder-text {
  font-size: var(--font-size-sm);
  max-width: 260px;
  margin: 0;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-md);
  padding-top: var(--spacing-xl);
}

.loader-dots {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.loading-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.error-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
}

.error-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  max-width: 260px;
}

.retry-button {
  padding: var(--spacing-xs) var(--spacing-lg);
  border-radius: var(--radius-lg);
  border: none;
  background: var(--color-accent);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.empty-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
}

.empty-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  max-width: 260px;
}

.markets-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.market-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  border-radius: var(--radius-lg);
  background: var(--color-bg-secondary);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
}

.market-item:active {
  transform: scale(0.99);
}

.coin-main {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.coin-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.coin-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.coin-icon-placeholder {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.coin-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.coin-name-row {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-xs);
}

.coin-name {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.coin-symbol {
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.coin-sub-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.coin-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.coin-price {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.coin-change {
  font-size: var(--font-size-xs);
}

.coin-change.positive {
  color: #22c55e;
}

.coin-change.negative {
  color: #ef4444;
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-md);
}

.load-more-button {
  min-width: 160px;
}

.load-more-button:disabled {
  opacity: 0.6;
  cursor: default;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@media screen and (min-width: 924px) {
  .markets-container {
    height: 100vh;
    padding-left: 132px;
    padding-bottom: var(--spacing-lg);
    overflow: hidden;
  }

  .markets-content {
    margin-top: 0;
  }

  .markets-main {
    flex: 1;
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: var(--spacing-lg);
    overflow: hidden;
  }

  .markets-left {
    overflow-y: auto;
    padding-right: var(--spacing-md);
  }

  .markets-right {
    display: block;
    border-radius: var(--radius-lg);
    background: var(--color-bg-secondary);
    padding: var(--spacing-lg);
    overflow-y: auto;
  }

  .markets-right ::-webkit-scrollbar {
    width: 4px;
  }

  .markets-right ::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.6);
    border-radius: 999px;
  }
}

@media (max-width: 480px) {
  .markets-container {
    padding: var(--spacing-md);
    padding-bottom: 100px;
  }
}
</style>


